import React, { useState, useEffect, useCallback, useRef } from "react";
import { useCss } from "../context/CssContext";
import { useStatic } from "../context/StaticContext";
import config from "../config";

const BannerCarousel = () => {
  const { bannerImages: cssBanners } = useCss();
  const { bonusBanners, bonusBannerVisible } = useStatic();
  
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Müşteri "Banner Ekleme" ve "CSS Düzenleyici" bannerlarını birleştiriyor
  let combinedBanners = [];
  
  if (bonusBanners && bonusBanners.length > 0) {
    const activeBonusBanners = bonusBanners
      .filter((b) => b.status === 'active')
      .sort((a, b) => a.order - b.order)
      .map((b) => {
        let imgUrl = b.image;
        if (!imgUrl.startsWith('http') && !imgUrl.startsWith('data:')) {
          const prefix = config.imgUrl && config.imgUrl.endsWith('/') ? config.imgUrl : `${config.imgUrl || ''}/`;
          const cleanedImg = imgUrl.startsWith('/') ? imgUrl.slice(1) : imgUrl;
          imgUrl = `${prefix}${cleanedImg}`;
        }
        return imgUrl;
      });
    combinedBanners = [...combinedBanners, ...activeBonusBanners];
  }
  
  if (cssBanners && cssBanners.length > 0) {
    combinedBanners = [...combinedBanners, ...cssBanners];
  }

  const bannerImages = combinedBanners;
  const bannerEnabled = bonusBannerVisible;

  const totalSlides = bannerImages ? bannerImages.length : 0;
  const isVisible = bannerEnabled && totalSlides > 0;

  // Otomatik geçiş zamanlayıcısı
  const startAutoPlay = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (totalSlides <= 1) return;
    timerRef.current = setInterval(() => {
      setIsTransitioning(true);
      setActiveIndex((prev) => (prev + 1) % totalSlides);
      setTimeout(() => setIsTransitioning(false), 500);
    }, 4000);
  }, [totalSlides]);

  useEffect(() => {
    if (!isVisible) return;
    startAutoPlay();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startAutoPlay, isVisible]);

  // activeIndex totalSlides'ı aşmasın
  useEffect(() => {
    if (activeIndex >= totalSlides && totalSlides > 0) {
      setActiveIndex(0);
    }
  }, [totalSlides, activeIndex]);

  // Banner gösterilmemesi gereken durumlar
  if (!isVisible) {
    return null;
  }

  const goToSlide = (index) => {
    if (index === activeIndex || isTransitioning) return;
    setIsTransitioning(true);
    setActiveIndex(index);
    startAutoPlay();
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const goNext = () => {
    if (isTransitioning) return;
    goToSlide((activeIndex + 1) % totalSlides);
  };

  const goPrev = () => {
    if (isTransitioning) return;
    goToSlide((activeIndex - 1 + totalSlides) % totalSlides);
  };

  // Touch desteği
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
  };

  return (
    <div
      className="banner-carousel"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slider Track */}
      <div
        className="banner-carousel-track"
        style={{ transform: `translateX(-${activeIndex * 100}%)` }}
      >
        {bannerImages.map((img, idx) => (
          <div key={idx} className="banner-carousel-slide">
            <img src={img} alt={`Banner ${idx + 1}`} className="banner-carousel-img" />
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {totalSlides > 1 && (
        <>
          <button
            className="banner-carousel-arrow banner-carousel-arrow--prev"
            onClick={goPrev}
            aria-label="Önceki"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            className="banner-carousel-arrow banner-carousel-arrow--next"
            onClick={goNext}
            aria-label="Sonraki"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Dots */}
      {totalSlides > 1 && (
        <div className="banner-carousel-dots">
          {bannerImages.map((_, idx) => (
            <button
              key={idx}
              className={`banner-carousel-dot${idx === activeIndex ? " active" : ""}`}
              onClick={() => goToSlide(idx)}
              aria-label={`Banner ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerCarousel;
